import { type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Send,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";
import { useBusinessAnalytics } from "@/lib/stats";
import { appLocale } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { SimpleSelect } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

/** Resolve a design-token CSS var (space-separated RGB) to an rgb() string. */
function rgbVar(name: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v ? `rgb(${v})` : fallback;
}

// Locale-aware so the EN UI doesn't show ru-RU thousands/decimal separators.
const fmtUsd = (v: number | null | undefined) =>
  v == null ? "—" : `$${v.toLocaleString(appLocale(), { maximumFractionDigits: 2 })}`;
const fmtNum = (v: number | null | undefined) =>
  v == null ? "—" : v.toLocaleString(appLocale());
const fmtPct = (v: number | null | undefined) =>
  v == null ? "—" : `${(v * 100).toFixed(1)}%`;

export function BusinessAnalytics() {
  const { t } = useTranslation();
  const [days, setDays] = useState(30);
  const q = useBusinessAnalytics(days);

  const colors = useMemo(
    () => ({
      accent: rgbVar("--color-accent", "rgb(139 92 246)"),
      success: rgbVar("--color-success", "rgb(16 185 129)"),
      danger: rgbVar("--color-danger", "rgb(239 68 68)"),
      warning: rgbVar("--color-warning", "rgb(245 158 11)"),
      muted: rgbVar("--color-text-muted", "rgb(161 161 170)"),
      border: rgbVar("--color-border", "rgb(39 39 42)"),
      bg: rgbVar("--color-bg-elevated", "rgb(39 39 42)"),
      fg: rgbVar("--color-text-primary", "rgb(250 250 250)"),
    }),
    [],
  );

  // Fill the day range so the area chart is continuous even with sparse data.
  const series = useMemo(() => {
    const map = new Map((q.data?.invites_daily ?? []).map((d) => [d.day, d]));
    const out: { label: string; attempts: number; leads: number }[] = [];
    const today = new Date();
    for (let i = (q.data?.days ?? days) - 1; i >= 0; i--) {
      const dt = new Date(today);
      dt.setDate(today.getDate() - i);
      const key = dt.toISOString().slice(0, 10);
      const found = map.get(key);
      out.push({
        label: `${dt.getDate()}.${dt.getMonth() + 1}`,
        attempts: found?.attempts ?? 0,
        leads: found?.leads ?? 0,
      });
    }
    return out;
  }, [q.data, days]);

  const outcomes = useMemo(
    () =>
      (q.data?.outcomes ?? []).slice(0, 10).map((o) => ({
        name: t(`inviter.attempt_status.${o.status}`, o.status),
        count: o.count,
        isSuccess: o.status === "added",
      })),
    [q.data, t],
  );

  const sellers = useMemo(
    () =>
      (q.data?.cost_per_lead_by_seller ?? [])
        .filter((s) => s.cost_per_lead_usd != null)
        .slice(0, 8)
        .map((s) => ({ name: s.source_label, cpl: s.cost_per_lead_usd ?? 0, leads: s.leads })),
    [q.data],
  );

  const tooltipStyle = {
    background: colors.bg,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    color: colors.fg,
    fontSize: 12,
  };

  const k = q.data?.kpis;

  // Before the first campaign every number here is 0 or "—", and the six
  // tiles plus an empty chart plus two "нет данных" panels filled a whole
  // screen the operator had to scroll past. Collapse to one line until there
  // is something to show.
  const hasActivity =
    !!k &&
    (k.attempts_total > 0 ||
      k.leads > 0 ||
      k.invites_sent_total > 0 ||
      k.total_spent_usd > 0);

  if (!q.isLoading && !hasActivity) {
    return (
      <section className="space-y-4">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-fg-primary">
          <TrendingUp size={18} strokeWidth={2} aria-hidden="true" className="text-accent" />
          {t("dashboard.business.title")}
        </h2>
        <Card>
          <CardContent className="py-6 text-center text-sm text-fg-muted">
            {t("dashboard.business.empty")}
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-fg-primary">
          <TrendingUp size={18} strokeWidth={2} aria-hidden="true" className="text-accent" />
          {t("dashboard.business.title")}
        </h2>
        {/* The period drives the daily chart only; every figure below is
            all-time. Say so next to the control, or "7 days" reads as the
            scope of the whole section. */}
        <div className="flex items-center gap-2">
          <span className="hidden text-xs text-fg-muted sm:inline">
            {t("dashboard.business.range_label")}
          </span>
          <div className="w-44">
            <SimpleSelect
              value={String(days)}
              onValueChange={(v) => setDays(Number(v))}
              options={[
                { value: "7", label: t("dashboard.business.range_7") },
                { value: "30", label: t("dashboard.business.range_30") },
                { value: "90", label: t("dashboard.business.range_90") },
              ]}
            />
          </div>
        </div>
      </div>
      <p className="text-xs text-fg-muted">{t("dashboard.business.kpi_scope")}</p>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiTile
          icon={<Target size={16} aria-hidden="true" />}
          tone="accent"
          label={t("dashboard.business.cost_per_lead")}
          value={q.isLoading ? null : fmtUsd(k?.cost_per_lead_usd)}
          sub={t("dashboard.business.cost_per_lead_sub")}
        />
        <KpiTile
          icon={<Users size={16} aria-hidden="true" />}
          tone="success"
          label={t("dashboard.business.leads")}
          value={q.isLoading ? null : fmtNum(k?.leads)}
          sub={t("dashboard.business.leads_sub")}
        />
        <KpiTile
          icon={<Send size={16} aria-hidden="true" />}
          tone="info"
          label={t("dashboard.business.invites_per_account")}
          value={q.isLoading ? null : fmtNum(k?.avg_invites_per_account)}
          sub={t("dashboard.business.invites_per_account_sub", { count: k?.inviter_accounts ?? 0 })}
        />
        <KpiTile
          icon={<TrendingUp size={16} aria-hidden="true" />}
          tone="info"
          label={t("dashboard.business.conversion")}
          value={q.isLoading ? null : fmtPct(k?.conversion_rate)}
          sub={t("dashboard.business.conversion_sub", { count: k?.attempts_total ?? 0 })}
        />
      </div>

      {/* Daily trend */}
      <Card>
        <CardContent className="pt-5">
          <p className="mb-3 text-sm font-medium text-fg-primary">
            {t("dashboard.business.daily_title")}
          </p>
          {q.isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gAttempts" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={colors.accent} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={colors.accent} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gLeads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={colors.success} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={colors.success} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.border} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: colors.muted }} stroke={colors.border} interval="preserveStartEnd" minTickGap={24} />
                <YAxis tick={{ fontSize: 11, fill: colors.muted }} stroke={colors.border} allowDecimals={false} width={48} />
                <RTooltip contentStyle={tooltipStyle} labelStyle={{ color: colors.muted }} />
                <Area
                  type="monotone"
                  dataKey="attempts"
                  name={t("dashboard.business.attempts")}
                  stroke={colors.accent}
                  fill="url(#gAttempts)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="leads"
                  name={t("dashboard.business.leads")}
                  stroke={colors.success}
                  fill="url(#gLeads)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {/* Outcomes */}
        <Card>
          <CardContent className="pt-5">
            <p className="mb-3 text-sm font-medium text-fg-primary">
              {t("dashboard.business.outcomes_title")}
            </p>
            {q.isLoading ? (
              <Skeleton className="h-56 w-full" />
            ) : outcomes.length === 0 ? (
              <p className="py-10 text-center text-sm text-fg-muted">
                {t("dashboard.business.no_data")}
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(180, outcomes.length * 34)}>
                <BarChart data={outcomes} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.border} horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: colors.muted }} stroke={colors.border} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: colors.muted }} stroke={colors.border} width={110} />
                  <RTooltip contentStyle={tooltipStyle} cursor={{ fill: colors.border, opacity: 0.3 }} />
                  <Bar dataKey="count" name={t("dashboard.business.count")} radius={[0, 4, 4, 0]}>
                    {outcomes.map((o, i) => (
                      <Cell key={i} fill={o.isSuccess ? colors.success : colors.danger} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Cost per lead by seller */}
        <Card>
          <CardContent className="pt-5">
            <p className="mb-3 text-sm font-medium text-fg-primary">
              {t("dashboard.business.cpl_by_seller_title")}
            </p>
            {q.isLoading ? (
              <Skeleton className="h-56 w-full" />
            ) : sellers.length === 0 ? (
              <p className="py-10 text-center text-sm text-fg-muted">
                {t("dashboard.business.cpl_no_data")}
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(180, sellers.length * 34)}>
                <BarChart data={sellers} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.border} horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: colors.muted }} stroke={colors.border} tickFormatter={(v) => `$${v}`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: colors.muted }} stroke={colors.border} width={140} />
                  <RTooltip contentStyle={tooltipStyle} cursor={{ fill: colors.border, opacity: 0.3 }} formatter={(value) => [`$${value}`, t("dashboard.business.cost_per_lead")]} />
                  <Bar dataKey="cpl" fill={colors.accent} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

const TONE_CLASS: Record<string, string> = {
  accent: "text-accent",
  success: "text-success",
  danger: "text-danger",
  info: "text-info",
  neutral: "text-fg-secondary",
};

function KpiTile({
  icon,
  tone,
  label,
  value,
  sub,
}: {
  icon: ReactNode;
  tone: keyof typeof TONE_CLASS;
  label: string;
  value: string | null;
  sub?: string;
}) {
  return (
    <Card>
      <CardContent className="space-y-1 p-4">
        <div className={`flex items-center gap-1.5 text-xs font-medium ${TONE_CLASS[tone]}`}>
          {icon}
          <span className="truncate text-fg-muted">{label}</span>
        </div>
        {value === null ? (
          <Skeleton className="h-7 w-20" />
        ) : (
          <p className="text-2xl font-bold tabular-nums text-fg-primary">{value}</p>
        )}
        {sub && <p className="text-xs leading-snug text-fg-muted">{sub}</p>}
      </CardContent>
    </Card>
  );
}
